import { ArrowRight, CarFront, Check, Clock, Luggage, MapPin, Plus, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { publicMedia } from "@/api";
import { pick, useLang } from "@/i18n";
import { GoldImage } from "@/components/GoldImage";

const Meta = ({ icon: Icon, label, value, testId }) => (
    <div className="flex items-center gap-2.5" data-testid={testId}>
        <Icon size={14} className="shrink-0 text-amber-400/90" />
        <span className="text-[10px] uppercase tracking-widest text-stone-500">{label}</span>
        <span className="ml-auto text-xs font-medium text-stone-200 text-right">{value}</span>
    </div>
);

/** Card di un transfer reale proveniente dal gestionale Tourvoyagers
 *  (GET /api/website/transfers): mostra solo i campi restituiti, mai dati inventati. */
export const TransferCard = ({ item, index }) => {
    const { lang, t } = useLang();
    const tx = t.transfers;
    const name = pick(item.name, lang);
    const from = pick(item.from, lang);
    const to = pick(item.to, lang);
    const desc = pick(item.description, lang);
    const wait = pick(item.wait, lang);
    const conditions = pick(item.conditions, lang);
    const price = item.price != null && item.price !== "" ? `€ ${Number(item.price).toLocaleString("it-IT")}` : null;
    return (
        <Link to={`/transfer/${item.id}`} data-testid={`transfer-item-${item.id || index}`}
            className="gold-card group flex flex-col overflow-hidden rounded-2xl transition-all hover:!border-amber-400/60 hover:shadow-[0_12px_40px_rgba(0,0,0,0.5)]">
            <div className="relative h-44 shrink-0 overflow-hidden">
                <GoldImage src={publicMedia(item.image_url, true)} alt={name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0c]/80 to-transparent" />
                {price && (
                    <span className="absolute right-4 top-4 rounded-full border border-amber-400/60 bg-[#0b0b0c]/85 px-3.5 py-1.5 text-xs font-bold text-amber-300" data-testid={`transfer-price-${item.id || index}`}>
                        {price}{item.priceType ? <span className="ml-1 font-normal text-stone-400">{item.priceType}</span> : null}
                    </span>
                )}
            </div>
            <div className="flex flex-1 flex-col p-5">
                <h3 className="font-display text-xl text-amber-50">{name}</h3>
                {(from || to) && (
                    <p className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-amber-200/80" data-testid={`transfer-route-${item.id || index}`}>
                        <MapPin size={12} className="text-amber-400" /> {from} <ArrowRight size={11} className="text-stone-500" /> {to}
                    </p>
                )}
                {desc && <p className="mt-2 text-xs leading-relaxed text-stone-400 line-clamp-3">{desc}</p>}

                <div className="mt-4 space-y-2 border-t border-amber-500/10 pt-4">
                    {item.duration && <Meta icon={Clock} label={tx.fldDuration} value={pick(item.duration, lang)} testId={`transfer-duration-${item.id || index}`} />}
                    {item.vehicle && <Meta icon={CarFront} label={tx.fldVehicle} value={item.vehicle} testId={`transfer-vehicle-${item.id || index}`} />}
                    {item.capacity != null && <Meta icon={Users} label={tx.fldPassengers} value={item.capacity} testId={`transfer-capacity-${item.id || index}`} />}
                    {item.luggage != null && <Meta icon={Luggage} label={tx.fldLuggage} value={pick(item.luggage, lang)} testId={`transfer-luggage-${item.id || index}`} />}
                </div>

                {item.stops?.length > 0 && (
                    <p className="mt-3 text-[11px] text-stone-400">
                        <span className="text-stone-500 uppercase tracking-widest text-[9px]">{tx.fldStops}: </span>
                        {item.stops.map((s) => pick(s, lang)).join(" · ")}
                    </p>
                )}
                {item.includes?.length > 0 && (
                    <ul className="mt-3 space-y-1.5" data-testid={`transfer-includes-${item.id || index}`}>
                        {item.includes.map((s, i) => (
                            <li key={i} className="flex gap-2 text-[11px] text-stone-300"><Check size={12} className="mt-0.5 shrink-0 text-amber-400" />{pick(s, lang)}</li>
                        ))}
                    </ul>
                )}
                {item.supplements?.length > 0 && (
                    <ul className="mt-2 space-y-1.5" data-testid={`transfer-supplements-${item.id || index}`}>
                        {item.supplements.map((s, i) => (
                            <li key={i} className="flex gap-2 text-[11px] text-stone-400"><Plus size={12} className="mt-0.5 shrink-0 text-amber-500/80" />{pick(s, lang)}</li>
                        ))}
                    </ul>
                )}
                {wait && <p className="mt-3 text-[11px] text-stone-400"><span className="text-stone-500 uppercase tracking-widest text-[9px]">{tx.fldWait}: </span>{wait}</p>}
                {conditions && <p className="mt-1.5 text-[10px] leading-relaxed text-stone-500">{conditions}</p>}

                <div className="mt-5 flex-1" />
                <span data-testid={`transfer-request-${item.id || index}`}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 px-6 py-3 text-[10px] font-bold uppercase tracking-[0.14em] text-black transition-all group-hover:bg-amber-300">
                    {tx.request} <ArrowRight size={13} />
                </span>
            </div>
        </Link>
    );
};
